import useGetApi from 'src/api/useGetApi';
import { CurrencyCode } from '@erp/shared-types';

/**
 * Company metadata structure
 *
 * IMPORTANT: Currently includes temporary currency handling.
 * defaultCurrency is used to pre-populate currency in CSV workflows.
 * defaultTaxRate is used to pre-populate tax rate in supplier payment workflows.
 *
 * TODO: Future refactoring needed for proper multi-currency support.
 * @see packages/shared-types/src/currency.ts for currency constants
 */
export interface CompanyMetadata {
  defaultCurrency?: CurrencyCode;
  defaultTaxRate?: number; // Tax rate as percentage (e.g., 13 for 13%)
  [key: string]: unknown;
}

export interface CompanyDTO {
  id: string;
  organizationId: string;
  name: string;
  email?: string | null;
  primaryAddressId?: string | null;
  supplierId?: string | null;
  metadata?: CompanyMetadata | null;
  createdAt: string;
  updatedAt: string;

  // Related data (included based on query params)
  primaryAddress?: {
    id: string;
    addressLine1: string;
    addressLine2?: string | null;
    locality: string;
    administrativeArea?: string | null;
    postalCode?: string | null;
    countryIso31661Alpha2: string;
  } | null;

  supplier?: {
    id: string;
    name: string;
    contactName?: string | null;
    email?: string | null;
    phone?: string | null;
    status: string;
    taxId?: string | null;
    paymentTerms?: string | null;
    metadata?: Record<string, unknown> | null;
    tags?: Array<{
      id: string;
      name: string;
      description: string;
    }>;
  } | null;

  aliases?: Array<{
    id: string;
    alias: string;
    entityType: string;
    entityId: string;
    addedByUserId?: string | null;
    createdAt: string;
    updatedAt: string;
  }>;

  enabledModules?: string[];
}

export interface QueryParams {
  include?: Array<
    'addresses' | 'supplier' | 'aliases' | 'modules' | 'contacts'
  >;
}

type ApiResponse = {
  company: CompanyDTO;
};

const useCompany = (
  companyId: string | undefined,
  queryParams?: QueryParams,
  enabled = true,
) => {
  return useGetApi<ApiResponse>({
    endpoint: `core/companies/${companyId}`,
    queryParameters: queryParams,
    options: {
      enabled: !!companyId && enabled,
    },
  });
};

export default useCompany;
